import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Check, Plus } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@design-system';
import { selectClient } from '@client/model/selectors';

import {
  ASSET_TYPES,
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  type AssetTypeId,
} from '@/features/guided-financial-planning/config';
import { useIsSessionComplete } from '@/features/guided-financial-planning/context';
import {
  useFundingSources,
  usePartnerAllocation,
  DEFAULT_CUSTOM_CLIENT_PERCENT,
} from '@/features/guided-financial-planning/hooks';
import { AccountCard, type AllocationCallbacks } from './account-card';

export interface FundingSourceDialogEditPayload {
  mode: 'edit';
  fundingSourceId: string;
  assetTypeId: AssetTypeId;
  label: string;
  selfFundingSource: SelfFundingSource;
}

export interface FundingSourceDialogAddPayload {
  mode: 'add';
  assetTypeId: AssetTypeId;
  label: string;
}

export type FundingSourceDialogPayload =
  | FundingSourceDialogEditPayload
  | FundingSourceDialogAddPayload;

interface FundingSourceDialogProps {
  open: boolean;
  onClose: () => void;
  payload: FundingSourceDialogPayload | null;
}

export function FundingSourceDialog({ open, onClose, payload }: FundingSourceDialogProps) {
  const isSessionComplete = useIsSessionComplete();
  const client = useSelector(selectClient);
  const clientId = client?.clientId;
  const {
    hasPartner,
    client: partnerClientData,
    partnerClient,
    getCardAllocationState,
    applyCardAllocation,
  } = usePartnerAllocation();

  const clientName = partnerClientData?.clientFirstName ?? 'You';
  const partnerName = partnerClient?.clientFirstName ?? 'Partner';

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose],
  );

  const allocation = useMemo((): AllocationCallbacks | undefined => {
    if (!hasPartner || !payload) return undefined;
    const isEditMode = payload.mode === 'edit';
    const fundingSourceId = isEditMode ? payload.fundingSourceId : null;
    const selfFundingSource = isEditMode ? payload.selfFundingSource : undefined;

    return {
      hasPartner: true,
      clientName,
      partnerName,
      initialState: fundingSourceId
        ? getCardAllocationState(fundingSourceId)
        : { enabled: false, option: 'mirror' as const, customClientPercent: DEFAULT_CUSTOM_CLIENT_PERCENT },
      onApply: (state, overrides) =>
        applyCardAllocation(
          overrides?.fundingSourceId ?? fundingSourceId ?? '',
          payload.assetTypeId,
          overrides?.sourceData ?? selfFundingSource!,
          state,
        ),
    };
  }, [hasPartner, payload, clientName, partnerName, getCardAllocationState, applyCardAllocation]);

  if (!clientId || !payload) return null;

  const isEditMode = payload.mode === 'edit';
  const dialogTitle = isEditMode ? `Edit ${payload.label}` : `Add ${payload.label}`;

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <AccountCard
          assetTypeId={payload.assetTypeId}
          label={payload.label}
          isExpanded={true}
          onToggle={() => {}}
          fundingSourceId={isEditMode ? payload.fundingSourceId : null}
          existingSource={isEditMode ? payload.selfFundingSource : undefined}
          onSaveComplete={onClose}
          disabled={isSessionComplete}
          hideHeader
          allocation={allocation}
        />
      </DialogContent>
    </Dialog>
  );
}

interface AddAccountButtonProps {
  onSelectAssetType: (assetTypeId: AssetTypeId, label: string) => void;
  disabled?: boolean;
}

export function AddAccountButton({ onSelectAssetType, disabled }: AddAccountButtonProps) {
  const fundingSources = useFundingSources();
  const selfFunding = fundingSources?.selfFunding ?? {};

  const usedAccountTypes = useMemo(
    () =>
      new Set(
        Object.values(selfFunding)
          .map(source => source.accountType)
          .filter(Boolean),
      ),
    [selfFunding],
  );

  const allAssetTypes = useMemo(
    () => ASSET_TYPES.filter(asset => asset.id !== 'existing-ltc-policy'),
    [],
  );

  const hasAvailable = allAssetTypes.some(
    asset => !usedAccountTypes.has(ASSET_TYPE_TO_ACCOUNT_TYPE[asset.id]),
  );

  if (!hasAvailable) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="w-full">
        <Button
          variant="outline"
          disabled={disabled}
          className="w-full border-dashed"
        >
          <Plus />
          Add Funding Account
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        className="w-(--radix-dropdown-menu-trigger-width)"
      >
        {allAssetTypes.map(asset => {
          const isUsed = usedAccountTypes.has(ASSET_TYPE_TO_ACCOUNT_TYPE[asset.id]);
          return (
            <DropdownMenuItem
              key={asset.id}
              disabled={isUsed}
              onSelect={() => onSelectAssetType(asset.id, asset.label)}
              className="flex items-center justify-between"
            >
              <span>{asset.label}</span>
              {isUsed && (
                <Badge
                  variant="secondary"
                  className="ml-2 px-1.5 py-0 text-xs"
                >
                  <Check className="mr-0.5 size-3" />
                  Added
                </Badge>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
